import { useState, useCallback, useRef, useEffect } from 'react';
import { getLoadingMessage } from '../components/Loading/LoadingComponents';

export const useLoading = (initialState = false, type = 'stories') => {
  const [isLoading, setIsLoading] = useState(initialState);
  const [loadingPhase, setLoadingPhase] = useState('initial');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);
  const progressRef = useRef(null);

  // Start loading with optional progressive phases
  const startLoading = useCallback((phases = ['initial']) => {
    setIsLoading(true);
    setError(null);
    setProgress(0);
    setLoadingPhase(phases[0] || 'initial');

    // If multiple phases, cycle through them
    if (phases.length > 1) {
      const phaseInterval = 1500; // 1.5 seconds per phase
      let currentPhaseIndex = 0;

      const cyclePhases = () => {
        currentPhaseIndex++;
        if (currentPhaseIndex < phases.length) {
          setLoadingPhase(phases[currentPhaseIndex]);
          setProgress((currentPhaseIndex / phases.length) * 100);
          timeoutRef.current = setTimeout(cyclePhases, phaseInterval);
        }
      };

      timeoutRef.current = setTimeout(cyclePhases, phaseInterval);
    }
  }, []);

  // Stop loading
  const stopLoading = useCallback(() => {
    setIsLoading(false);
    setLoadingPhase('complete');
    setProgress(100);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
  }, []);

  // Set error state
  const setLoadingError = useCallback((errorMessage) => {
    setError(errorMessage);
    setIsLoading(false);
    setProgress(0);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Manual progress update
  const updateProgress = useCallback((newProgress) => {
    setProgress(Math.min(100, Math.max(0, newProgress)));
  }, []);

  // Simulate realistic loading progress
  const simulateProgress = useCallback((duration = 3000) => {
    setProgress(0);
    const interval = 50; // Update every 50ms
    const steps = duration / interval;
    const increment = 100 / steps;
    let currentProgress = 0;

    progressRef.current = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        setProgress(100);
        clearInterval(progressRef.current);
        progressRef.current = null;
      } else {
        // Add some randomness to make it feel more natural
        const randomVariation = (Math.random() - 0.5) * 5;
        setProgress(Math.min(95, currentProgress + randomVariation));
      }
    }, interval);
  }, []);

  // Get current loading message
  const loadingMessage = getLoadingMessage(type, loadingPhase);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (progressRef.current) {
        clearInterval(progressRef.current);
      }
    };
  }, []);

  return {
    isLoading,
    loadingPhase,
    progress,
    error,
    loadingMessage,
    startLoading,
    stopLoading,
    setLoadingError,
    updateProgress,
    simulateProgress
  };
};

// Specialized hooks for different content types
export const useStoriesLoading = () => {
  return useLoading(false, 'stories');
};

export const useEventsLoading = () => {
  return useLoading(false, 'events');
};

export const useCommentsLoading = () => {
  return useLoading(false, 'comments');
};

export const useSearchLoading = () => {
  return useLoading(false, 'search');
};

// Async operation wrapper with loading states
export const useAsyncOperation = (type = 'stories') => {
  const loading = useLoading(false, type);

  const execute = useCallback(async (
    operation, 
    { 
      phases = ['initial'], 
      showProgress = false,
      progressDuration = 3000,
      onSuccess,
      onError 
    } = {}
  ) => {
    try {
      loading.startLoading(phases);
      
      if (showProgress) {
        loading.simulateProgress(progressDuration);
      }

      const result = await operation();
      
      loading.stopLoading();
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error.message || 'An error occurred';
      loading.setLoadingError(errorMessage);
      
      if (onError) {
        onError(error);
      }
      
      throw error;
    }
  }, [loading]);

  return {
    ...loading,
    execute
  };
};

export default useLoading;
